import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import type { CareerServicesDashboardSection } from "../queries";

type AllSectionsOption = {
  id: null;
  name: string;
};

type SectionOption = CareerServicesDashboardSection | AllSectionsOption;

type Props = {
  sections: CareerServicesDashboardSection[];
  sectionId: string | null;
  onSectionChange: (sectionId: string | null) => void;
  selectionRequired?: boolean;
};

const ALL_SECTIONS_OPTION: AllSectionsOption = {
  id: null,
  name: "All sections"
};

export default function CareerServicesDashboardSectionPicker({
  sections,
  sectionId,
  onSectionChange,
  selectionRequired = false
}: Props) {
  const options = useMemo<SectionOption[]>(
    () => (selectionRequired ? sections : [ALL_SECTIONS_OPTION, ...sections]),
    [sections, selectionRequired]
  );
  const selectedOption = useMemo(
    () => options.find((section) => section.id === sectionId) ?? (selectionRequired ? undefined : ALL_SECTIONS_OPTION),
    [options, sectionId, selectionRequired]
  );
  const selectedOptionName = selectedOption?.name ?? "";
  const [query, setQuery] = useState(selectedOptionName);

  useEffect(() => {
    setQuery(selectedOptionName);
  }, [selectedOptionName]);

  if (!selectionRequired && sections.length <= 1) {
    return null;
  }

  const filteredOptions =
    query === selectedOptionName
      ? options
      : options.filter((section) => section.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="w-full max-w-sm" data-testid="career-services-dashboard-section-picker">
      <Autocomplete
        variant="searchableDropdown"
        label="Section"
        query={query}
        data={filteredOptions}
        placeholder={selectionRequired && !sectionId ? "Select a section" : undefined}
        required={selectionRequired}
        menuClassName="max-h-[20rem]"
        onChangeText={setQuery}
        onSelect={(section) => {
          onSectionChange(section.id);
          setQuery(section.name);
        }}
        reset={() => {
          setQuery(selectedOptionName);
        }}
        renderItem={(section) => (
          <div className="flex items-center border-bottom">
            <div className="mr-4 flex items-center justify-center rounded-full bg-sys-brand-secondary-container p-2">
              <Icon name="domain" size={24} />
            </div>
            <div className="text-body-large">{section.name}</div>
          </div>
        )}
      />
    </div>
  );
}
